import React, { useState } from 'react'
import moment from 'moment/moment'
import { Button, Modal, notification, Tag, Tooltip, Popconfirm } from 'antd'
import {
  PlusSquareOutlined,
  DeleteOutlined,
  SendOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  StopOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import styled from 'styled-components/macro'
import { stripTransientProps } from 'src/utils/stripTransientProps'
import SaveToDataplusBtn from './SaveToDataplusBtn'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectVatPreference,
  VAT_PREFERENCE,
} from 'src/features/VatPreferenceSlice'
import { vatLabelSuffix } from 'src/utils/vatPreference'
import {
  deletedOrder,
  undoVoidReceiptingOrder,
  voidReceiptingOrder,
} from 'src/features/ReceiptingSlice'
import { Link } from 'react-router-dom'
import SaveReceiptConfirmModal from './components/SaveReceiptConfirmModal'
import ActualDeliveryDateModal from './components/ActualDeliveryDateModal'
import VoidOrderModal from './components/VoidOrderModal'
import {
  formatActualDeliveryDateForDisplay,
  parseActualDeliveryDate,
} from 'src/utils/actualDeliveryDate'
import { saveAs } from 'file-saver'
import { buildCsvString } from 'src/utils/csv'
import { getReceiptingEditTypeLabel } from 'src/utils/receiptingEditTypeLabels'
import {
  getGoodsReceiptingInvoiceIdentity,
  getLinkedInvoicesForReceiptingOrder,
  INVOICE_STATUS,
  isInCreditWorkflow,
  isReceipted,
} from 'src/utils/reconciliationHealth'
import {
  getHollowTagProps,
  getInvoicePreviewUrl,
} from 'src/utils/invoiceUploadHelpers'
import config from 'src/config'
import {
  GR_GUIDED_ACTIONS,
  getGrActionToneStyles,
  getGrAcceptDisabledReason,
  getGrLineOutcomeProgress,
  hasGrLineEditsReady,
  isGrAcceptDisabled,
  canAddProductOnGoodsReceipting,
  getAddProductDisabledReason,
} from 'src/utils/goodsReceiptingActionHelpers'
import {
  getGoodsReceiptingStatusDisplay,
  getOrderStatusDisplay,
  isReceiptNotSaved,
  withRetroEditedBadge,
} from 'src/utils/goodsReceiptingStatusHelpers'
import {
  canRetrospectivelyEditReceipts,
  RETRO_RECEIPT_LOCKED_COPY,
} from 'src/utils/permissions'
import useUserRole from 'src/hooks/useUserRole'
import { useCanAccess } from 'src/hooks/useIsBlocked'
import {
  FOOD_SAFETY_DELIVERY_CHECK_HINT,
  FOOD_SAFETY_DELIVERY_CHECK_LABEL,
  buildFoodSafetyDeliveryCheckPath,
  foodSafetyDeliveryCheckTarget,
} from 'src/utils/foodSafetyDeliveryCheckLink'
import { getGoodsReceiptingPlacedDisplay } from 'src/utils/goodsReceiptingPlacedHelpers'
import {
  canShowMatchOnGoodsReceiptingOrder,
  getGoodsReceiptingMatchActionState,
  getGoodsReceiptingPoInvoiceListItems,
} from 'src/utils/goodsReceiptingMatchHelpers'
import { getInvoiceMatchActionMeta } from 'src/utils/invoiceMatchActionHelpers'
import {
  canDeleteManualGoodsReceiptingOrder,
  getDeleteManualGoodsReceiptingDisabledReason,
  GR_DELETE_MANUAL_COPY,
  isManualDashboardGoodsReceiptingOrder,
} from 'src/utils/goodsReceiptingDeleteHelpers'

import {
  canUndoVoidGoodsReceiptingOrder,
  canVoidGoodsReceiptingOrder,
  getUndoVoidGoodsReceiptingDisabledReason,
  getVoidGoodsReceiptingDisabledReason,
  GR_VOID_COPY,
  isVoidedGoodsReceiptingOrder,
} from 'src/utils/goodsReceiptingVoidHelpers'

const OrdersColumns = (
  errors,
  weightedItems,
  asOrderedLineIds = [],
  onSave,
  onFullAccept,
  onMarkDelivered,
  products,
  setOrdersToAdding,
  venueId,
  setModalShow,
  isDataplus,
  setInvoiceModalData,
  uploadedInvoices = [],
  expandOrderRow,
  invoiceRows = [],
  onMatchOrder,
  rematchingInvoiceId = null,
  onPoInvoiceClick = null,
  onVoidSuccess = null,
  onUndoVoidSuccess = null,
  onRetroEdit = null,
  isRetroEditActive = () => false,
) => {
  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue?.venue)
  const userName = useSelector(state => state.auth?.account?.name) || ''
  const vatPreference = useSelector(selectVatPreference)

  // Purchasing VAT toggle: Ordered/Receipted columns switch basis. Ex values
  // keep the exact pre-toggle sources; inc falls back to ex when a not-yet
  // redeployed backend omits the inc twins.
  const pickOrderedPennies = record =>
    vatPreference === VAT_PREFERENCE.INC
      ? record.totalSpendOrderedIncVat ?? record.totalSpendOrdered ?? 0
      : record.totalSpendOrdered ?? 0
  const pickReceivedPennies = record =>
    vatPreference === VAT_PREFERENCE.INC
      ? record.totalSpendReceivedVatIncPennies ??
        record.totalSpendReceivedIncVat ??
        record.totalSpendReceived ??
        record.totalSpend ??
        0
      : record.totalSpendReceivedVatExPennies ??
        record.totalSpendReceived ??
        record.totalSpend ??
        0
  const userRole = useUserRole()
  const canSeeFoodSafety = useCanAccess('food-safety')
  const canRetroEdit = canRetrospectivelyEditReceipts(
    userRole,
    venue?.customRoles || [],
  )
  const xero = venue?.xeroConnection
  const isSavingReconciliation = useSelector(
    state => state.receipting.isSavingReconciliation,
  )
  const isSavingCnRequest = useSelector(
    state => state.receipting.isSavingCnRequest,
  )
  const [saveReceiptModal, setSaveReceiptModal] = useState({
    visible: false,
    record: null,
  })
  const [acceptAsOrderedModal, setAcceptAsOrderedModal] = useState({
    visible: false,
    record: null,
  })
  const [markDeliveredModal, setMarkDeliveredModal] = useState({
    visible: false,
    record: null,
  })
  const [voidModal, setVoidModal] = useState({
    visible: false,
    record: null,
  })
  const [retroEditModal, setRetroEditModal] = useState({
    visible: false,
    record: null,
  })
  const [isVoiding, setIsVoiding] = useState(false)
  const [undoingKey, setUndoingKey] = useState(null)
  const [pendingDeliveredKeys, setPendingDeliveredKeys] = useState({})
  const [acceptingKey, setAcceptingKey] = useState(null)
  const resolveInvoiceIdentity = record =>
    getGoodsReceiptingInvoiceIdentity(record, uploadedInvoices, invoiceRows)

  const collectInvoicePreviewUrlsForOrder = record => {
    const urls = []
    const push = value => {
      const url = String(value || '').trim()
      if (url && !urls.includes(url)) urls.push(url)
    }
    const identity = resolveInvoiceIdentity(record)
    push(identity.primaryUrl)
    push(record?.invoiceUrl)
    for (const url of record?.attachedInvoicesUrls || []) push(url)
    for (const detail of record?.attachedInvoiceDetails || []) push(detail?.url)
    const expressBase = config?.apiGateway?.EXPRESS_URL || ''
    for (const invoice of getLinkedInvoicesForReceiptingOrder(
      record,
      uploadedInvoices,
    )) {
      push(getInvoicePreviewUrl(invoice, expressBase))
    }
    return urls
  }

  const getInvoiceLink = record => {
    const identity = resolveInvoiceIdentity(record)
    if (identity.primaryUrl) return identity.primaryUrl
    if (record.invoiceUrl) return record.invoiceUrl
    if (record?.attachedInvoicesUrls?.length)
      return record.attachedInvoicesUrls[0]
    return ''
  }

  const handleDownloadOrder = record => {
    try {
      const headers = [
        'Supplier Name',
        'PO Number',
        `Ordered Amount ${vatLabelSuffix(vatPreference)}`,
        `Receipted amount ${vatLabelSuffix(vatPreference)}`,
        'Placed',
        'Ordered Date',
        'Actual Delivery Date',
        'Invoice Date',
        'Order Status',
        'Receipting Status',
        'Invoice Status',
        'Last Edit Type',
        'Last Edit Date',
        'Last Edited By',
        'Invoice Link',
      ]
      const rows = [
        headers,
        [
          record?.supplier?.title || '',
          record?.purchaseOrderNumber || '',
          (pickOrderedPennies(record) / 100).toFixed(2),
          (pickReceivedPennies(record) / 100).toFixed(2),
          getGoodsReceiptingPlacedDisplay(record).label,
          record?.orderedDate
            ? moment(record.orderedDate).format('YYYY-MM-DD')
            : '',
          formatActualDeliveryDateForDisplay(record?.actualDeliveryDate),
          record?.invoiceDate
            ? moment(record.invoiceDate).format('YYYY-MM-DD')
            : '',
          getOrderStatusDisplay(record?.status) || '',
          record?.receiptingStatus || '',
          resolveInvoiceIdentity(record).displayLabel,
          getReceiptingEditTypeLabel(record?.receipting?.editType),
          record?.lastEdited
            ? moment(record.lastEdited).format('YYYY-MM-DD HH:mm')
            : '',
          record?.savedBy || '',
          getInvoiceLink(record),
        ],
      ]

      const csvString = buildCsvString(rows)
      const poNumber = (record?.purchaseOrderNumber || 'order')
        .toString()
        .replace(/[^a-zA-Z0-9-_]/g, '_')
      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      const filename = `goods-receipting_${poNumber}_${timestamp}.csv`

      const blob = new Blob([csvString], {
        type: 'text/csv;charset=utf-8;',
      })
      saveAs(blob, filename)

      notification.success({
        message: 'Download Started',
        description: 'Goods receipting data exported successfully.',
        placement: 'topRight',
      })
    } catch (error) {
      notification.error({
        message: 'Download Failed',
        description: 'Unable to export goods receipting data right now.',
        placement: 'topRight',
      })
    }
  }

  const findCurrentOrders = (record, array) => {
    const supplierId = record.supplier._id
    const PONumber = record.purchaseOrderNumber
    return array.filter(
      item =>
        item?.purchaseOrderNumber === PONumber && item.supplier === supplierId,
    )
  }
  const showNotesMessage = () => {
    notification.error({
      message: 'Please add comments to the text box',
      placement: 'topRight',
      top: 100,
    })
  }

  const handleSaveClick = record => {
    if (isSomethingElseError(record)) {
      showNotesMessage()
      return
    }
    setSaveReceiptModal({ visible: true, record })
  }

  const confirmSaveReceipt = (saveType, actualDeliveryDate) => {
    const record = saveReceiptModal.record
    if (!record) return
    onSave(record, saveType || 'reconciliation', null, actualDeliveryDate)
    setSaveReceiptModal({ visible: false, record: null })
  }

  const cancelSaveReceipt = () => {
    setSaveReceiptModal({ visible: false, record: null })
  }

  const ensureExpanded = record => {
    if (typeof expandOrderRow === 'function') {
      expandOrderRow(record)
    }
  }

  const getPoLineOrders = record => {
    const supplierId = record?.supplier?._id
    const po = record?.purchaseOrderNumber
    const loaded = (products || []).find(
      item =>
        item?.purchaseOrderNumber === po &&
        String(item?.supplier?._id || item?.supplier) === String(supplierId),
    )
    return loaded?.orders || []
  }

  const handleSaveReceipt = record => {
    ensureExpanded(record)
    const poLines = getPoLineOrders(record)
    const poLineIds = poLines.map(line => line._id)
    if (
      hasGrLineEditsReady(
        record,
        errors,
        weightedItems,
        asOrderedLineIds,
        poLineIds,
      )
    ) {
      handleSaveClick(record)
      return
    }
    notification.info({
      message: GR_GUIDED_ACTIONS.SAVE_RECEIPT.title,
      description: (() => {
        const progress = getGrLineOutcomeProgress(
          poLines,
          errors,
          asOrderedLineIds,
        )
        const base =
          'Expand the order below, mark lines As ordered or Report Error on each problem, then click Save receipt again.'
        if (progress.total === 0) return base
        const progressLine = `${progress.resolved} of ${progress.total} lines completed`
        const unresolvedLine =
          progress.unresolved > 0
            ? ` — ${progress.unresolved} line${
                progress.unresolved === 1 ? '' : 's'
              } still require${progress.unresolved === 1 ? 's' : ''} an outcome`
            : ''
        return `${progressLine}${unresolvedLine}. ${base}`
      })(),
      placement: 'topRight',
      top: 100,
      duration: 5,
    })
  }

  const runAcceptAsOrdered = async (record, actualDeliveryDate) => {
    if (!record) return
    const key = getOrderRowKey(record)
    if (!key || acceptingKey) return
    setAcceptingKey(key)
    try {
      await onFullAccept(record, actualDeliveryDate)
      setAcceptAsOrderedModal({ visible: false, record: null })
    } finally {
      setAcceptingKey(null)
    }
  }

  const handleAcceptAsOrdered = record => {
    if (!record || acceptingKey) return
    // Date already captured on Delivered — reuse it; don't ask again.
    const existing = parseActualDeliveryDate(record?.actualDeliveryDate)
    if (existing.ok) {
      runAcceptAsOrdered(record, existing.ymd)
      return
    }
    setAcceptAsOrderedModal({ visible: true, record })
  }

  const confirmAcceptAsOrdered = async actualDeliveryDate => {
    await runAcceptAsOrdered(acceptAsOrderedModal.record, actualDeliveryDate)
  }

  const cancelAcceptAsOrdered = () => {
    setAcceptAsOrderedModal({ visible: false, record: null })
  }

  const renderFoodSafetyDeliveryCheck = record => {
    if (!canSeeFoodSafety || !venueId || !record?.purchaseOrderNumber) {
      return null
    }
    return (
      <Tooltip placement='top' title={FOOD_SAFETY_DELIVERY_CHECK_HINT}>
        <FoodSafetyCheckLink
          to={buildFoodSafetyDeliveryCheckPath(
            venueId,
            foodSafetyDeliveryCheckTarget(record),
          )}
          data-testid={`gr-food-safety-check-${record.purchaseOrderNumber}`}
        >
          {FOOD_SAFETY_DELIVERY_CHECK_LABEL}
        </FoodSafetyCheckLink>
      </Tooltip>
    )
  }

  const getOrderRowKey = record =>
    `${record?.supplier?._id || ''}-${record?.purchaseOrderNumber || ''}`

  // Closed for normal check-in: receipted OR open credit/CN workflow.
  // Both must show Edit receipt for Admin / Head Chef GM (not Accept/Save).
  const isReceiptLockedForCheckIn = record =>
    isReceipted(record) || isInCreditWorkflow(record)

  const canShowDeliveredAction = record =>
    record?.status === 'Received' && !isReceiptLockedForCheckIn(record)

  const openMarkDeliveredModal = record => {
    const key = getOrderRowKey(record)
    if (!record || !key || pendingDeliveredKeys[key]) return
    setMarkDeliveredModal({ visible: true, record })
  }

  const confirmMarkDelivered = async actualDeliveryDate => {
    const record = markDeliveredModal.record
    if (!record || !actualDeliveryDate) return
    const key = getOrderRowKey(record)
    if (!key || pendingDeliveredKeys[key]) return

    setPendingDeliveredKeys(prev => ({ ...prev, [key]: true }))
    try {
      await onMarkDelivered(record, actualDeliveryDate)
      setMarkDeliveredModal({ visible: false, record: null })
    } finally {
      setPendingDeliveredKeys(prev => ({ ...prev, [key]: false }))
    }
  }

  const cancelMarkDelivered = () => {
    setMarkDeliveredModal({ visible: false, record: null })
  }
  const isSomethingElseError = record => {
    const currentOrders = findCurrentOrders(record, errors)
    if (currentOrders.length) {
      return currentOrders.find(
        item => item.error === 'Something Else' && !item.notes,
      )
    }
    return false
  }

  /**
   * Full-accept disable rules. Do NOT require products to be pre-loaded —
   * onFullAccept fetches them on click so users can Accept without expanding.
   */
  const isAcceptDisabled = record =>
    isGrAcceptDisabled(record, {
      errors,
      weightedItems,
      products,
      asOrderedLineIds,
    })

  const getAcceptTooltip = record =>
    getGrAcceptDisabledReason(record, {
      errors,
      weightedItems,
      products,
      asOrderedLineIds,
    }) || GR_GUIDED_ACTIONS.ACCEPT_AS_ORDERED.guidance

  const setOrderDataToAdding = record => {
    const { orderedDate, purchaseOrderNumber, placedVia } = record
    setOrdersToAdding({
      orderedDate,
      placedVia,
      purchaseOrderNumber,
      supplier: record.supplier._id,
      venue: venueId,
      isOrdered: true,
      supplierConfirmed: true,
    })
    setModalShow(true)
  }

  const columns = [
    {
      title: 'Supplier',
      dataIndex: 'supplier.title',
      // No fixed width — absorbs leftover space on wide screens.
      className: 'restrict-shrink supplier-col',
      ellipsis: true,
      onHeaderCell: () => ({ style: { minWidth: 96 } }),
      onCell: () => ({ style: { minWidth: 96 } }),
      render: (supplierTitle, record) => {
        const ableToSaveToDataplus =
          (record.status === 'Confirmed' || record.status === 'Changes') &&
          isDataplus &&
          !record.isOrderSavedToDataplus

        return (
          <SupplierCell>
            <SupplierName title={supplierTitle}>{supplierTitle}</SupplierName>
            <SupplierPo title={record.purchaseOrderNumber}>
              {record.purchaseOrderNumber}
            </SupplierPo>

            {ableToSaveToDataplus && (
              <SaveToDataplusBtn
                venueId={venueId}
                orderIds={record?.orderIds || []}
                PONumber={record.purchaseOrderNumber}
              />
            )}
          </SupplierCell>
        )
      },
    },
    {
      title: (
        <ColumnHeaderTitle>
          Ordered
          <span>{`amount ${vatLabelSuffix(vatPreference)}`}</span>
        </ColumnHeaderTitle>
      ),
      dataIndex: 'totalSpendOrdered',
      width: 84,
      className: 'restrict-shrink',
      render: (_, record) => (
        <CompactAmount>{`£${(pickOrderedPennies(record) / 100).toFixed(
          2,
        )}`}</CompactAmount>
      ),
    },
    {
      title: (
        <ColumnHeaderTitle>
          Receipted
          <span>{`amount ${vatLabelSuffix(vatPreference)}`}</span>
        </ColumnHeaderTitle>
      ),
      width: 168,
      className: 'restrict-shrink',
      render: (_, record) => {
        if (isReceiptNotSaved(record?.receiptingStatus)) {
          return <CompactAmount title='Not receipted yet'>—</CompactAmount>
        }
        const amount = (
          <span>{`£${(pickReceivedPennies(record) / 100).toFixed(2)}`}</span>
        )
        if (!record?.receiptedByWeight) {
          return <CompactAmount>{amount}</CompactAmount>
        }
        return (
          <CompactAmount>
            {amount}
            <WeightNote>
              {record.weightReceiptLabel || 'Receipted by weight'}
            </WeightNote>
          </CompactAmount>
        )
      },
    },
    {
      title: 'Placed',
      dataIndex: 'placedBy',
      width: 72,
      className: 'restrict-shrink',
      ellipsis: true,
      render: (_, record) => {
        const placed = getGoodsReceiptingPlacedDisplay(record)
        return (
          <Tooltip title={placed.tooltip}>
            <CompactText title={placed.tooltip}>{placed.label}</CompactText>
          </Tooltip>
        )
      },
    },
    {
      title: (
        <ColumnHeaderTitle>
          Ordered
          <span>date</span>
        </ColumnHeaderTitle>
      ),
      dataIndex: 'orderedDate',
      width: 86,
      className: 'restrict-shrink',
      render: date => (
        <CompactText>{moment(date).format('YYYY-MM-DD')}</CompactText>
      ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ColumnHeaderTitle>
            Actual
            <span>delivery</span>
          </ColumnHeaderTitle>
          <Tooltip
            placement='top'
            title='When goods physically arrived. Independent of order date and Goods Receipting confirmation time. Blank/Not Recorded for older receipts.'
          >
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', cursor: 'help', fontSize: 12 }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'actualDeliveryDate',
      width: 92,
      className: 'restrict-shrink',
      render: value => (
        <CompactText>{formatActualDeliveryDateForDisplay(value)}</CompactText>
      ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ColumnHeaderTitle>
            Invoice
            <span>date</span>
          </ColumnHeaderTitle>
          <Tooltip
            placement='top'
            title='Supplier invoice date when available — compare with Actual delivery for month-end. Blank when no invoice date is recorded on the receipt.'
          >
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', cursor: 'help', fontSize: 12 }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'invoiceDate',
      width: 86,
      className: 'restrict-shrink',
      render: value => (
        <CompactText>
          {value ? moment(value).format('YYYY-MM-DD') : '—'}
        </CompactText>
      ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ColumnHeaderTitle>
            Order
            <span>status</span>
          </ColumnHeaderTitle>
          <Tooltip
            placement='top'
            title={
              <span>
                <strong>Order status</strong> — supplier confirmation for this
                PO:
                <br />• <strong>Received by supplier</strong> — order reached
                the supplier; awaiting their confirmation
                <br />• <strong>Confirmed</strong> — supplier confirmed all
                lines
                <br />• <strong>Changes</strong> — some lines rejected or
                changed by supplier
              </span>
            }
          >
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', cursor: 'help', fontSize: 12 }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'status',
      width: 120,
      className: 'restrict-shrink',
      render: status => {
        // Aggregated supplier confirmation / order status for this PO
        // Values from backend: 'Received' | 'Confirmed' | 'Changes'
        let preset = 'orange'
        if (status === 'Confirmed') preset = 'green'
        else if (status === 'Changes') preset = 'magenta'

        return (
          <Tag {...getHollowTagProps(preset)}>
            {getOrderStatusDisplay(status)}
          </Tag>
        )
      },
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ColumnHeaderTitle>
            Receipting
            <span>status</span>
          </ColumnHeaderTitle>
          <Tooltip
            placement='top'
            title={
              <span>
                <strong>Receipting status</strong> — chef-facing labels over
                saved progress:
                <br />• <strong>Not receipted</strong> — not saved yet
                <br />• <strong>Partially receipted</strong> — some lines
                received; others still outstanding
                <br />• <strong>Receipted</strong> — delivery checked and saved
                <br />• <strong>Credit requested</strong> — receipted, waiting
                on supplier for credit (not “unreceipted”)
                <br />• <strong>Credit approved / rejected</strong> — supplier
                response on the credit request
              </span>
            }
          >
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', cursor: 'help', fontSize: 12 }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'receiptingStatus',
      width: 104,
      className: 'receipting-status-col',
      render: (receiptingStatus, record) => {
        // Stored values unchanged; labels are chef-facing only.
        const display = withRetroEditedBadge(
          getGoodsReceiptingStatusDisplay(
            receiptingStatus,
            record?.receipting || null,
          ),
          record?.retrospectivelyEdited,
        )

        return (
          <Tooltip title={display.tooltip}>
            <StatusStack>
              <Tag
                {...getHollowTagProps(display.primaryTag.color || 'default')}
              >
                {display.primaryLabel}
              </Tag>
              {(display.badge ? String(display.badge).split(' · ') : [])
                .map(part => part.trim())
                .filter(Boolean)
                .map((badgeLabel, index) => {
                  // Keep in sync with RETRO_EDITED_BADGE in status helpers.
                  const isRetroBadge = badgeLabel === 'Retrospectively edited'
                  const tagStyle = isRetroBadge
                    ? { color: 'purple' }
                    : display.badgeTag
                  return (
                    <Tag
                      key={`${badgeLabel}-${index}`}
                      {...getHollowTagProps(tagStyle?.color || 'orange')}
                    >
                      {badgeLabel}
                    </Tag>
                  )
                })}
            </StatusStack>
          </Tooltip>
        )
      },
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ColumnHeaderTitle>
            Reconciliation
            <span>status</span>
          </ColumnHeaderTitle>
          <Tooltip
            placement='top'
            title={
              <span>
                <strong>Reconciliation status</strong> — invoice attachment and
                whether Match &amp; reconcile is finished (3-way match):
                <br />• <strong>No invoice attached</strong> — no invoice file
                on this PO yet (same idea as Received on Invoices)
                <br />• <strong>Invoice attached — #123</strong> — linked file,
                not yet reconciled. When more than one invoice touches this PO,
                each is listed (open leftovers first).
                <br />• <strong>Invoice reconciled — #123</strong> — Match &amp;
                reconcile finished for this PO
                <br />• Click an invoice line to open Match / Remove
                <br />• <strong>Sent to Xero</strong> — invoice sent to your
                accounting platform (counts as reconciled)
              </span>
            }
          >
            <InfoCircleOutlined
              style={{ color: '#8c8c8c', cursor: 'help', fontSize: 12 }}
            />
          </Tooltip>
        </span>
      ),
      dataIndex: 'invoiceStatus',
      width: 120,
      className: 'invoice-status-col',
      render: (_, record) => {
        const identity = resolveInvoiceIdentity(record)
        const poInvoices = getGoodsReceiptingPoInvoiceListItems(
          record,
          invoiceRows,
        )
        // Match Order status / Receipting status: antd Tag colour presets
        // (green border + green text), not custom filled chips.
        let tagColor
        if (
          identity.status === INVOICE_STATUS.SENT_TO_XERO ||
          identity.status === INVOICE_STATUS.RECONCILED
        ) {
          tagColor = 'green'
        } else if (identity.status === INVOICE_STATUS.ATTACHED) {
          tagColor = 'blue'
        }

        return (
          <InvoiceStatusCell title={identity.displayLabel}>
            <Tag color={tagColor}>{identity.label}</Tag>
            {poInvoices.length > 0 ? (
              <PoInvoiceList>
                {poInvoices.map(item => (
                  <PoInvoiceLine
                    key={item.id}
                    type='button'
                    $muted={item.isClosed}
                    title={
                      item.canMatch
                        ? `Open Match for ${item.lineLabel}`
                        : item.lineLabel
                    }
                    onClick={e => {
                      e.stopPropagation()
                      if (onPoInvoiceClick) {
                        onPoInvoiceClick(record, item.invoice)
                        return
                      }
                      onMatchOrder?.(record, [item.invoice], {
                        focusInvoice: item.invoice,
                      })
                    }}
                  >
                    {item.lineLabel}
                  </PoInvoiceLine>
                ))}
              </PoInvoiceList>
            ) : identity.detail ? (
              <InvoiceStatusDetail title={identity.detail}>
                {identity.detail}
              </InvoiceStatusDetail>
            ) : null}
            {identity.hasFile ? (
              <InvoiceStatusLink
                type='link'
                size='small'
                onClick={() => setInvoiceModalData(record)}
              >
                View file{identity.attachedCount > 1 ? 's' : ''}
              </InvoiceStatusLink>
            ) : null}
          </InvoiceStatusCell>
        )
      },
    },
    {
      title: 'Last edit',
      dataIndex: 'savedBy',
      width: 96,
      className: 'last-edit-col',
      render: (savedBy, record) => {
        return (
          <LastEditCell>
            <div>
              {getReceiptingEditTypeLabel(record?.receipting?.editType)}
            </div>
            <div>
              {record?.lastEdited
                ? moment(record.lastEdited).format('YYYY-MM-DD HH:mm')
                : ''}
            </div>
            <div>{savedBy ? `${savedBy}` : ''}</div>
          </LastEditCell>
        )
      },
    },
    {
      title: 'Actions',
      className: 'gr-actions-col',
      width: 156,
      // Do not use fixed:'right' with expandedRowRender — antd 3 cannot keep
      // fixed Action cells aligned to the parent row when nested lines expand.
      onCell: () => ({
        style: { verticalAlign: 'top' },
        className: 'gr-actions-cell',
      }),
      render: (_, record) => {
        const key = getOrderRowKey(record)

        if (isVoidedGoodsReceiptingOrder(record)) {
          const canUndo = canUndoVoidGoodsReceiptingOrder(
            record,
            uploadedInvoices,
          )
          const undoBlockedReason = getUndoVoidGoodsReceiptingDisabledReason(
            record,
            uploadedInvoices,
          )
          const isUndoing = undoingKey === key
          const voidedLabel = record.voidReason
            ? `Voided · ${record.voidReason}`
            : 'Voided'
          return (
            <ActionsBox>
              <ActionPrimaryColumn>
                <ReconciledHint title={record.voidNote || voidedLabel}>
                  {voidedLabel}
                </ReconciledHint>
                {canUndo ? (
                  <Popconfirm
                    disabled={isUndoing}
                    title={
                      <span>
                        <strong>Undo void?</strong>
                        <br />
                        This order will return to Goods receipting.
                      </span>
                    }
                    placement='topRight'
                    onConfirm={async () => {
                      setUndoingKey(key)
                      try {
                        const result = await dispatch(
                          undoVoidReceiptingOrder({
                            supplierId: record?.supplier?._id,
                            purchaseOrderNumber: record?.purchaseOrderNumber,
                            venueId,
                          }),
                        )
                        if (result && typeof onUndoVoidSuccess === 'function') {
                          onUndoVoidSuccess(record)
                        }
                      } finally {
                        setUndoingKey(null)
                      }
                    }}
                    okText='Undo void'
                    cancelText='Cancel'
                  >
                    <Tooltip placement='top' title={GR_VOID_COPY.undoTooltip}>
                      <GuidedActionButton
                        type='default'
                        loading={isUndoing}
                        aria-label={GR_VOID_COPY.undoLabel}
                      >
                        <UndoOutlined /> {GR_VOID_COPY.undoLabel}
                      </GuidedActionButton>
                    </Tooltip>
                  </Popconfirm>
                ) : (
                  <Tooltip
                    placement='top'
                    title={undoBlockedReason || GR_VOID_COPY.undoBlockedWindow}
                  >
                    <span>
                      <GuidedActionButton
                        type='default'
                        disabled
                        aria-label='Undo void disabled'
                      >
                        <UndoOutlined /> {GR_VOID_COPY.undoLabel}
                      </GuidedActionButton>
                    </span>
                  </Tooltip>
                )}
              </ActionPrimaryColumn>
            </ActionsBox>
          )
        }

        const isMarkDeliveredLoading = !!pendingDeliveredKeys[key]
        const isAccepting = acceptingKey === key
        const acceptAction = GR_GUIDED_ACTIONS.ACCEPT_AS_ORDERED
        const saveReceiptAction = GR_GUIDED_ACTIONS.SAVE_RECEIPT
        const acceptDisabled = isAcceptDisabled(record) || Boolean(acceptingKey)
        const matchState = getGoodsReceiptingMatchActionState(
          record,
          invoiceRows,
        )
        const matchCandidates = matchState.candidates
        const primaryMatchInvoice = matchState.primaryInvoice
        const matchAction = primaryMatchInvoice
          ? getInvoiceMatchActionMeta(primaryMatchInvoice)
          : null
        const isMatchLoading =
          Boolean(rematchingInvoiceId) &&
          matchCandidates.some(inv => inv.id === rematchingInvoiceId)
        const showMatchAction =
          canShowMatchOnGoodsReceiptingOrder(record) &&
          matchState.kind !== 'none'
        const renderMatchAction = () => {
          if (!showMatchAction) return null
          if (matchState.kind === 'reconciled') {
            return (
              <Tooltip title={matchState.tooltip}>
                <InvoiceReconciledHint>Reconciled</InvoiceReconciledHint>
              </Tooltip>
            )
          }
          return (
            <>
              {matchState.showLinkedNotReconciledHint ? (
                <LinkedNotReconciledHint>
                  Linked — not reconciled yet
                </LinkedNotReconciledHint>
              ) : null}
              {matchState.leftoverHint ? (
                <LeftoverMatchHint title={matchState.leftoverHint}>
                  {matchState.leftoverHintShort || matchState.leftoverHint}
                </LeftoverMatchHint>
              ) : null}
              {matchState.remainingHint ? (
                <LeftoverMatchHint title={matchState.remainingHint}>
                  {matchState.remainingHintShort || matchState.remainingHint}
                </LeftoverMatchHint>
              ) : null}
              <Tooltip title={matchState.tooltip}>
                <GuidedActionButton
                  type='default'
                  $toneStyles={
                    matchAction?.styles ||
                    getGrActionToneStyles(GR_GUIDED_ACTIONS.SAVE_RECEIPT.tone)
                  }
                  disabled={Boolean(rematchingInvoiceId)}
                  loading={isMatchLoading}
                  onClick={() => onMatchOrder?.(record, matchCandidates)}
                  aria-label={matchState.buttonLabel}
                >
                  {matchState.buttonLabel}
                </GuidedActionButton>
              </Tooltip>
            </>
          )
        }
        const hasInvoice =
          Boolean(record.invoiceUrl) ||
          Boolean(record?.attachedInvoicesUrls?.length > 0)
        const showXero =
          Boolean(xero?.accessToken) &&
          (record.status === 'Confirmed' || record.status === 'Changes')
        const isManualOrder = isManualDashboardGoodsReceiptingOrder(record)
        const canDeleteManual = canDeleteManualGoodsReceiptingOrder(record)
        const deleteBlockedReason =
          getDeleteManualGoodsReceiptingDisabledReason(record)
        const canVoid = canVoidGoodsReceiptingOrder(record, uploadedInvoices)
        const voidBlockedReason = getVoidGoodsReceiptingDisabledReason(
          record,
          uploadedInvoices,
        )

        if (record.status === 'Received') {
          const canVoidReceived = canVoidGoodsReceiptingOrder(
            record,
            uploadedInvoices,
          )
          const voidBlockedReceived = getVoidGoodsReceiptingDisabledReason(
            record,
            uploadedInvoices,
          )
          return !canShowDeliveredAction(record) ? (
            <WaitingText>Waiting on supplier approval</WaitingText>
          ) : (
            <ActionsBox>
              <ActionPrimaryRow>
                <Tooltip
                  placement='top'
                  title='Confirm delivery and record the Actual Delivery Date so you can receipt this order'
                >
                  <ActionTextButton
                    type='primary'
                    loading={isMarkDeliveredLoading}
                    disabled={isMarkDeliveredLoading}
                    onClick={() => openMarkDeliveredModal(record)}
                  >
                    Delivered
                  </ActionTextButton>
                </Tooltip>
              </ActionPrimaryRow>
              <ActionSecondaryRow>
                {canVoidReceived ? (
                  <Tooltip placement='top' title={GR_VOID_COPY.actionTooltip}>
                    <ActionIconButton
                      type='danger'
                      ghost
                      onClick={() => setVoidModal({ visible: true, record })}
                      aria-label='Void order'
                    >
                      <StopOutlined />
                    </ActionIconButton>
                  </Tooltip>
                ) : (
                  <Tooltip
                    placement='top'
                    title={voidBlockedReceived || GR_VOID_COPY.actionTooltip}
                  >
                    <span>
                      <ActionIconButton
                        type='danger'
                        ghost
                        disabled
                        aria-label='Void order disabled'
                      >
                        <StopOutlined />
                      </ActionIconButton>
                    </span>
                  </Tooltip>
                )}
              </ActionSecondaryRow>
            </ActionsBox>
          )
        }

        return (
          <ActionsBox>
            {!isReceiptLockedForCheckIn(record) || isRetroEditActive(record) ? (
              <ActionPrimaryColumn>
                <Tooltip title={getAcceptTooltip(record)}>
                  <GuidedActionButton
                    type='default'
                    $toneStyles={getGrActionToneStyles(acceptAction.tone)}
                    disabled={acceptDisabled}
                    loading={isAccepting}
                    onClick={() => handleAcceptAsOrdered(record)}
                    aria-label={acceptAction.title}
                  >
                    {acceptAction.label}
                  </GuidedActionButton>
                </Tooltip>
                <Tooltip title={saveReceiptAction.guidance}>
                  <GuidedActionButton
                    type='default'
                    $toneStyles={getGrActionToneStyles(saveReceiptAction.tone)}
                    onClick={() => handleSaveReceipt(record)}
                    aria-label={saveReceiptAction.title}
                  >
                    {saveReceiptAction.label}
                  </GuidedActionButton>
                </Tooltip>
                {renderMatchAction()}
              </ActionPrimaryColumn>
            ) : canRetroEdit ? (
              <ActionPrimaryColumn>
                <Tooltip title='Reopen this completed receipt to correct quantities, statuses or prices. Changes are recorded as a retrospective edit.'>
                  <GuidedActionButton
                    type='default'
                    icon='edit'
                    onClick={() => setRetroEditModal({ visible: true, record })}
                    aria-label='Edit receipt'
                  >
                    Edit receipt
                  </GuidedActionButton>
                </Tooltip>
                {renderMatchAction()}
                {renderFoodSafetyDeliveryCheck(record)}
              </ActionPrimaryColumn>
            ) : (
              <ActionPrimaryColumn>
                <Tooltip
                  title={`${RETRO_RECEIPT_LOCKED_COPY.title} — ${RETRO_RECEIPT_LOCKED_COPY.message}`}
                >
                  <ReconciledHint>Receipted</ReconciledHint>
                </Tooltip>
                {renderMatchAction()}
                {renderFoodSafetyDeliveryCheck(record)}
              </ActionPrimaryColumn>
            )}

            <ActionSecondaryRow>
              {(() => {
                const canAddProduct = canAddProductOnGoodsReceipting(record)
                const addProductTitle = canAddProduct
                  ? 'Add product'
                  : getAddProductDisabledReason(record) ||
                    'Can’t add products on this order.'
                return (
                  <Tooltip placement='top' title={addProductTitle}>
                    <span>
                      <ActionIconButton
                        type='default'
                        disabled={!canAddProduct}
                        onClick={() => {
                          if (!canAddProduct) return
                          setOrderDataToAdding(record)
                        }}
                        aria-label='Add product'
                      >
                        <PlusSquareOutlined />
                      </ActionIconButton>
                    </span>
                  </Tooltip>
                )
              })()}
              <Tooltip placement='top' title='Download'>
                <ActionIconButton
                  type='default'
                  onClick={() => handleDownloadOrder(record)}
                  aria-label='Download'
                >
                  <DownloadOutlined />
                </ActionIconButton>
              </Tooltip>
              {showXero
                ? (() => {
                    const isInXero = Boolean(
                      record.xeroInvoiceId || record.sentToXero,
                    )
                    const hasXeroError = (
                      record.attachedInvoiceDetails || []
                    ).some(d => d?.status === 'errorSendingToXero')
                    return (
                      <>
                        <Tooltip
                          placement='top'
                          title={
                            isInXero
                              ? 'Sent to Xero'
                              : hasXeroError
                              ? 'Not in Xero — previous send failed (force send available)'
                              : 'Not in Xero — send bill'
                          }
                        >
                          {isInXero ? (
                            <span>
                              <ActionIconButton
                                type='default'
                                disabled
                                aria-label='Sent to Xero'
                              >
                                <SendOutlined />
                              </ActionIconButton>
                              <XeroBadge $sent>Sent to Xero</XeroBadge>
                            </span>
                          ) : (
                            <Link
                              to={{
                                pathname: `/v/${venueId}/purchasing/receipting/xero`,
                                state: {
                                  order: (() => {
                                    const previewUrls =
                                      collectInvoicePreviewUrlsForOrder(record)
                                    return {
                                      ...record,
                                      invoiceUrl:
                                        record.invoiceUrl ||
                                        previewUrls[0] ||
                                        null,
                                      attachedInvoicesUrls:
                                        record.attachedInvoicesUrls?.length > 0
                                          ? record.attachedInvoicesUrls
                                          : previewUrls,
                                    }
                                  })(),
                                  invoicePreviewUrls:
                                    collectInvoicePreviewUrlsForOrder(record),
                                  forceHint: hasXeroError,
                                },
                              }}
                            >
                              <ActionIconButton
                                type='default'
                                aria-label='Send to Xero'
                              >
                                <SendOutlined />
                              </ActionIconButton>
                              <XeroBadge>Not in Xero</XeroBadge>
                            </Link>
                          )}
                        </Tooltip>
                      </>
                    )
                  })()
                : null}
              {hasInvoice ? (
                <Tooltip placement='top' title='View invoice'>
                  <ActionIconButton
                    type='default'
                    onClick={() => setInvoiceModalData(record)}
                    aria-label='View invoice'
                  >
                    <EyeOutlined />
                  </ActionIconButton>
                </Tooltip>
              ) : null}
              {canVoid ? (
                <Tooltip placement='top' title={GR_VOID_COPY.actionTooltip}>
                  <ActionIconButton
                    type='danger'
                    ghost
                    onClick={() => setVoidModal({ visible: true, record })}
                    aria-label='Void order'
                  >
                    <StopOutlined />
                  </ActionIconButton>
                </Tooltip>
              ) : (
                <Tooltip
                  placement='top'
                  title={voidBlockedReason || GR_VOID_COPY.actionTooltip}
                >
                  <span>
                    <ActionIconButton
                      type='danger'
                      ghost
                      disabled
                      aria-label='Void order disabled'
                    >
                      <StopOutlined />
                    </ActionIconButton>
                  </span>
                </Tooltip>
              )}
              {isManualOrder ? (
                canDeleteManual ? (
                  <Popconfirm
                    title='Are you sure you want to delete the order?'
                    placement='topRight'
                    onConfirm={() => {
                      const supplierId = record?.supplier?._id
                      const PONumber = record?.purchaseOrderNumber
                      dispatch(deletedOrder(supplierId, PONumber, venueId))
                    }}
                    okText='Yes'
                    cancelText='No'
                  >
                    <Tooltip title={GR_DELETE_MANUAL_COPY.deleteTooltip}>
                      <ActionIconButton type='danger' aria-label='Delete order'>
                        <DeleteOutlined />
                      </ActionIconButton>
                    </Tooltip>
                  </Popconfirm>
                ) : (
                  <Tooltip
                    placement='top'
                    title={
                      deleteBlockedReason ||
                      GR_DELETE_MANUAL_COPY.blockedTooltip
                    }
                  >
                    <span>
                      <ActionIconButton
                        type='danger'
                        disabled
                        aria-label='Delete order disabled'
                      >
                        <DeleteOutlined />
                      </ActionIconButton>
                    </span>
                  </Tooltip>
                )
              ) : null}
            </ActionSecondaryRow>
          </ActionsBox>
        )
      },
    },
  ]

  return {
    columns,
    modal: (
      <>
        <SaveReceiptConfirmModal
          visible={saveReceiptModal.visible}
          record={saveReceiptModal.record}
          editedReceipting={errors}
          asOrderedLineIds={asOrderedLineIds}
          lineOrders={getPoLineOrders(saveReceiptModal.record)}
          weightedItems={weightedItems}
          onConfirm={confirmSaveReceipt}
          onCancel={cancelSaveReceipt}
          loading={isSavingReconciliation || isSavingCnRequest}
        />
        <ActualDeliveryDateModal
          visible={markDeliveredModal.visible}
          record={markDeliveredModal.record}
          title='Mark as delivered'
          okText='Mark as delivered'
          onConfirm={confirmMarkDelivered}
          onCancel={cancelMarkDelivered}
          loading={Boolean(
            markDeliveredModal.record &&
              pendingDeliveredKeys[getOrderRowKey(markDeliveredModal.record)],
          )}
        />
        <ActualDeliveryDateModal
          visible={acceptAsOrderedModal.visible}
          record={acceptAsOrderedModal.record}
          title='Accept as ordered'
          okText='Accept as ordered'
          onConfirm={confirmAcceptAsOrdered}
          onCancel={cancelAcceptAsOrdered}
          loading={Boolean(acceptingKey)}
        />
        <VoidOrderModal
          visible={voidModal.visible}
          record={voidModal.record}
          uploadedInvoices={uploadedInvoices}
          loading={isVoiding}
          onCancel={() => setVoidModal({ visible: false, record: null })}
          onConfirm={async ({ reason, note }) => {
            const record = voidModal.record
            if (!record) return
            setIsVoiding(true)
            try {
              const result = await dispatch(
                voidReceiptingOrder({
                  supplierId: record?.supplier?._id,
                  purchaseOrderNumber: record?.purchaseOrderNumber,
                  voidReason: reason,
                  voidNote: note,
                  voidedBy: userName,
                  voidedFromReceiptingStatus: record?.receiptingStatus || '',
                  venueId,
                }),
              )
              if (result) {
                if (typeof onVoidSuccess === 'function') {
                  onVoidSuccess(record)
                }
                setVoidModal({ visible: false, record: null })
              }
            } finally {
              setIsVoiding(false)
            }
          }}
        />
        <Modal
          visible={retroEditModal.visible}
          title='Edit a completed receipt?'
          okText='Continue'
          cancelText='Cancel'
          okButtonProps={{ danger: true }}
          onCancel={() => setRetroEditModal({ visible: false, record: null })}
          onOk={() => {
            const record = retroEditModal.record
            setRetroEditModal({ visible: false, record: null })
            if (record && typeof onRetroEdit === 'function') {
              onRetroEdit(record)
            }
          }}
        >
          <p>
            This order has already been receipted. Any changes you make will be
            recorded as a retrospective edit and may affect stock, spend,
            reconciliation and reporting.
          </p>
          <p>
            The corrected values replace the original receipt everywhere, and
            the audit trail keeps who edited it, when, and what changed.
          </p>
        </Modal>
      </>
    ),
  }
}

const ACTION_CONTROL_HEIGHT = 28
/** Width of icon-pair strip / Delivered button alignment. */
const ACTION_STRIP_MIN_WIDTH = ACTION_CONTROL_HEIGHT * 2 + 6

const ColumnHeaderTitle = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  white-space: normal;

  > span {
    font-weight: 400;
    color: rgba(0, 0, 0, 0.65);
  }
`

const ActionsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 4px 6px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  /* Keep the control strip with the parent row — do not stretch into expanded nested lines */
  position: sticky;
  top: 0;
  align-self: flex-start;
`

const ActionPrimaryColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
`

const ActionPrimaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
`

const ActionSecondaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
`

const XeroBadge = styled.span`
  display: inline-block;
  margin-left: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${p => (p.$sent ? '#389e0d' : '#ad4e00')};
  vertical-align: middle;
`

const ActionTextButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: ${ACTION_CONTROL_HEIGHT}px;
    min-width: ${ACTION_STRIP_MIN_WIDTH}px;
    margin: 0;
    padding: 0 8px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }
`

const GuidedActionButton = styled(stripTransientProps(Button))`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: auto;
    min-height: ${ACTION_CONTROL_HEIGHT}px;
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: 4px 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    white-space: normal;
    text-align: center;
    color: ${props => props.$toneStyles?.color || '#135200'};
    background: ${props => props.$toneStyles?.background || '#f6ffed'};
    border-color: ${props => props.$toneStyles?.border || '#52c41a'};
    border-width: 2px;

    &:hover,
    &:focus {
      color: ${props => props.$toneStyles?.color || '#135200'};
      background: ${props => props.$toneStyles?.activeBackground || '#d9f7be'};
      border-color: ${props => props.$toneStyles?.activeBorder || '#389e0d'};
    }

    &[disabled] {
      opacity: 0.55;
    }
  }
`

const ActionIconButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${ACTION_CONTROL_HEIGHT}px;
    min-width: ${ACTION_CONTROL_HEIGHT}px;
    height: ${ACTION_CONTROL_HEIGHT}px;
    margin: 0;
    padding: 0;
    line-height: 1;
  }

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    line-height: 0;
    font-size: 14px;
  }
`

const WaitingText = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
`

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: 100%;
  min-width: 0;

  .ant-tag {
    margin: 0;
    max-width: 100%;
    white-space: normal;
    word-break: break-word;
    line-height: 1.25;
    height: auto;
    padding: 0 6px;
  }
`

const ReconciledHint = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #135200;
  line-height: 1.2;
`

const FoodSafetyCheckLink = styled(Link)`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: #135200;
  text-decoration: underline;
  overflow-wrap: anywhere;
`

const InvoiceReconciledHint = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #52c41a;
  line-height: 1.2;
  padding: 4px 0;
`

const LinkedNotReconciledHint = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #ad6800;
  line-height: 1.2;
`

const LeftoverMatchHint = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #873800;
  line-height: 1.25;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
`

const PoInvoiceList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  max-width: 100%;
  min-width: 0;
`

const PoInvoiceLine = styled.button`
  display: block;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 11px;
  line-height: 1.3;
  color: ${props => (props.$muted ? '#8c8c8c' : '#0958d9')};
  cursor: pointer;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${props => (props.$muted ? '#595959' : '#003eb3')};
  }
`

const SupplierCell = styled.div`
  min-width: 0;
  max-width: 100%;
`

const SupplierName = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SupplierPo = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CompactAmount = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  font-variant-numeric: tabular-nums;
`

const WeightNote = styled.span`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55);
`

const CompactText = styled.span`
  display: inline-block;
  max-width: 100%;
  font-size: 12px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LastEditCell = styled.div`
  font-size: 11px;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.65);
  max-width: 100%;
  min-width: 0;

  > div {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`

const InvoiceStatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;

  .ant-tag {
    margin: 0;
    max-width: 100%;
    white-space: normal;
    word-break: break-word;
    line-height: 1.25;
    height: auto;
    padding: 0 6px;
  }
`

const InvoiceStatusDetail = styled.span`
  display: block;
  max-width: 100%;
  font-size: 11px;
  line-height: 1.3;
  color: #595959;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const InvoiceStatusLink = styled(Button)`
  && {
    height: auto;
    padding: 0;
    font-size: 11px;
    line-height: 1.3;
  }
`

export default OrdersColumns
